/**
 * Utility functions for handling type conversions consistently across the application
 */

/**
 * Converts a string or number ID to a number
 * @param id - The ID to convert (string, number, or undefined)
 * @returns The ID as a number, or undefined if the input is undefined or cannot be parsed
 */
export const toNumberId = (id: string | number | undefined): number | undefined => {
  if (id === undefined) return undefined;
  if (typeof id === 'number') return id;
  const parsed = parseInt(id, 10);
  return isNaN(parsed) ? undefined : parsed;
};

/**
 * Converts a string or number ID to a string
 * @param id - The ID to convert (string, number, or undefined)
 * @returns The ID as a string, or undefined if the input is undefined
 */
export const toStringId = (id: string | number | undefined): string | undefined => {
  if (id === undefined) return undefined;
  return String(id);
};

/**
 * Safely compares two IDs regardless of their type (string or number)
 * @param id1 - First ID to compare
 * @param id2 - Second ID to compare
 * @returns True if the IDs are equal, false otherwise
 */
export const areIdsEqual = (
  id1: string | number | undefined, 
  id2: string | number | undefined
): boolean => {
  if (id1 === undefined || id2 === undefined) return false;
  return String(id1) === String(id2);
};
